import { getSocket, emitEvent } from './socketClient';
import { logger } from '../../utils/logger';
import { ADMIN_SOCKET_EVENTS } from '../../utils/constants';
import { 
  invalidateCache, 
  updateMeetingCache, 
  markTabAsUpdated 
} from '../api/adminApi';

// Track processed events to avoid duplicate processing
const processedEvents = new Map<string, number>();
const EVENT_EXPIRY = 5000; // 5 seconds

// Regular cleanup of expired events
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of processedEvents.entries()) {
    if (now - timestamp > EVENT_EXPIRY) {
      processedEvents.delete(key);
    }
  }
}, 30000); // Clean up every 30 seconds

// Helper to get meeting ID from various data formats
const getMeetingId = (data: any): string | null => {
  if (!data) return null;
  
  // Handle array format
  if (Array.isArray(data) && data.length > 0) {
    data = data[0];
  }
  
  // Handle nested meeting object
  if (data && data.meeting) {
    data = data.meeting;
  }
  
  // Extract ID based on common property names
  return data._id || data.meetingId || data.id || null;
};

// Initialize admin socket connection
export const initAdminSocket = (): void => {
  try {
    const socket = getSocket();
    
    if (!socket) {
      logger.warn('Cannot initialize admin socket: No socket connection');
      return;
    }
    
    // Join admin room
    socket.emit('admin:join');
    
    logger.info('Admin socket initialized');
  } catch (error: any) {
    logger.error(`Admin socket initialization error: ${error.message}`);
  }
};

// Safely emit events with rate limiting
const safelyEmitEvent = (eventName: string, data: any = {}) => {
  const cacheKey = `emit:${eventName}:${JSON.stringify(data)}`;
  const now = Date.now();
  
  // Don't emit the same event more than once per second
  if (processedEvents.has(cacheKey) && now - processedEvents.get(cacheKey)! < 1000) {
    logger.info(`Skipping duplicate emit of ${eventName}`);
    return;
  }
  
  processedEvents.set(cacheKey, now);
  emitEvent(eventName, data);
};

// Listen for admin-specific socket events
export const listenToAdminEvents = (
  callbacks: {
    onMeetingCreated?: (data: any) => void;
    onMeetingUpdated?: (data: any) => void;
    onMeetingApproved?: (data: any) => void;
    onMeetingRejected?: (data: any) => void;
    onUserRoleChanged?: (data: any) => void;
    onUserStatusChanged?: (data: any) => void;
    onPendingCount?: (data: any) => void;
  }
): () => void => {
  const socket = getSocket();
  
  if (!socket) {
    logger.warn('Cannot listen to admin events: No socket connection');
    return () => {};
  }
  
  // Create enhanced callbacks that update cache first and deduplicate events
  const enhancedMeetingCreated = (data: any) => {
    // Deduplicate events based on meeting ID
    const meetingId = getMeetingId(data);
    
    if (meetingId) {
      const eventKey = `created:${meetingId}`;
      const now = Date.now();
      
      // Skip if this event was already processed recently
      if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
        console.log(`Skipping duplicate meeting created event for ${meetingId}`);
        return;
      }
      
      // Mark this event as processed
      processedEvents.set(eventKey, now);
    }
    
    // Log the event
    console.log('Meeting created event received:', data);
    
    // Mark affected tabs for refresh with HIGH PRIORITY
    markTabAsUpdated('pending', true); // True = high priority
    markTabAsUpdated('all', true);     // True = high priority
    
    // Update cache directly - but only attempt once
    updateMeetingCache(data).then(updatedData => {
      console.log('Cache updated with new meeting data', updatedData ? 'successfully' : 'failed');
    }).catch(error => {
      console.error('Error updating cache with meeting data:', error);
      // Fallback to simple invalidation if update fails
      invalidateCache('admin:meetings:pending:');
      invalidateCache('admin:meetings:all:');
    });
    
    // Then call original callback
    if (callbacks.onMeetingCreated) {
      callbacks.onMeetingCreated(data);
    }
  };
  
  // Enhanced callback for meeting updates with deduplication
  const enhancedMeetingUpdated = (data: any) => {
    // Deduplicate events based on meeting ID
    const meetingId = getMeetingId(data);
    
    if (meetingId) {
      const eventKey = `updated:${meetingId}`;
      const now = Date.now();
      
      // Skip if this event was already processed recently
      if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
        console.log(`Skipping duplicate meeting updated event for ${meetingId}`);
        return;
      }
      
      // Mark this event as processed
      processedEvents.set(eventKey, now);
    }
    
    // Log the event
    console.log('Meeting updated event received:', data);
    
    // We need to identify which tab this meeting belongs to
    const status = data.status || (data.meeting && data.meeting.status) || 'all';
    
    // Mark the affected tab for refresh with HIGH PRIORITY
    markTabAsUpdated(status, true);
    // Also mark 'all' tab since it contains all meetings
    markTabAsUpdated('all', true);
    
    // Try to update the specific meeting in cache - only attempt once
    if (data.meeting) {
      updateMeetingCache(data.meeting).catch(error => {
        console.error('Error updating meeting in cache:', error);
        // Fallback to invalidation
        invalidateCache(`admin:meetings:${status}:`);
        invalidateCache('admin:meetings:all:');
      });
    } else {
      // If we don't have detailed meeting data, just invalidate
      invalidateCache(`admin:meetings:${status}:`);
      invalidateCache('admin:meetings:all:');
    }
    
    // Then call original callback
    if (callbacks.onMeetingUpdated) {
      callbacks.onMeetingUpdated(data);
    }
  };
  
  // Enhanced callback for meeting approved with deduplication
  const enhancedMeetingApproved = (data: any) => {
    // Deduplicate events based on meeting ID
    const meetingId = getMeetingId(data);
    
    if (meetingId) {
      const eventKey = `approved:${meetingId}`;
      const now = Date.now();
      
      // Skip if this event was already processed recently
      if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
        console.log(`Skipping duplicate meeting approved event for ${meetingId}`);
        return;
      }
      
      // Mark this event as processed
      processedEvents.set(eventKey, now);
    }
    
    console.log('Meeting approved event received:', data);
    
    // Mark affected tabs with HIGH PRIORITY
    markTabAsUpdated('pending', true);
    markTabAsUpdated('approved', true);
    markTabAsUpdated('all', true);
    
    // Targeted invalidation for status change - only do once
    invalidateCache('admin:meetings:pending:');
    invalidateCache('admin:meetings:approved:');
    invalidateCache('admin:meetings:all:');
    
    if (callbacks.onMeetingApproved) {
      callbacks.onMeetingApproved(data);
    }
  };
  
  // Enhanced callback for meeting rejected with deduplication
  const enhancedMeetingRejected = (data: any) => {
    // Deduplicate events based on meeting ID
    const meetingId = getMeetingId(data);
    
    if (meetingId) {
      const eventKey = `rejected:${meetingId}`;
      const now = Date.now();
      
      // Skip if this event was already processed recently
      if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
        console.log(`Skipping duplicate meeting rejected event for ${meetingId}`);
        return;
      }
      
      // Mark this event as processed
      processedEvents.set(eventKey, now);
    }
    
    console.log('Meeting rejected event received:', data);
    
    // Mark affected tabs with HIGH PRIORITY
    markTabAsUpdated('pending', true);
    markTabAsUpdated('rejected', true);
    markTabAsUpdated('all', true);
    
    // Targeted invalidation for status change - only do once
    invalidateCache('admin:meetings:pending:');
    invalidateCache('admin:meetings:rejected:');
    invalidateCache('admin:meetings:all:');
    
    if (callbacks.onMeetingRejected) {
      callbacks.onMeetingRejected(data);
    }
  };
  
  // Register event handlers
  if (callbacks.onMeetingCreated) {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_CREATED, enhancedMeetingCreated);
  } else {
    // Still register to update cache even if no callback
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_CREATED, (data) => {
      // Deduplicate events based on meeting ID
      const meetingId = getMeetingId(data);
      
      if (meetingId) {
        const eventKey = `created:${meetingId}`;
        const now = Date.now();
        
        // Skip if this event was already processed recently
        if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
          console.log(`Skipping duplicate meeting created event for ${meetingId}`);
          return;
        }
        
        // Mark this event as processed
        processedEvents.set(eventKey, now);
      }
      
      // Mark tabs as needing HIGH PRIORITY refresh
      markTabAsUpdated('pending', true);
      markTabAsUpdated('all', true);
      
      // Try to update cache directly - one attempt only
      updateMeetingCache(data).catch(() => {
        // Fallback to invalidation
        invalidateCache('admin:meetings:pending:');
        invalidateCache('admin:meetings:all:');
      });
    });
  }
  
  // Register handler for meeting updates with deduplication
  if (callbacks.onMeetingUpdated) {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_UPDATED, enhancedMeetingUpdated);
  } else {
    // Still register to update cache even if no callback
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_UPDATED, (data) => {
      // Deduplicate events based on meeting ID
      const meetingId = getMeetingId(data);
      
      if (meetingId) {
        const eventKey = `updated:${meetingId}`;
        const now = Date.now();
        
        // Skip if this event was already processed recently
        if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
          console.log(`Skipping duplicate meeting updated event for ${meetingId}`);
          return;
        }
        
        // Mark this event as processed
        processedEvents.set(eventKey, now);
      }
      
      const status = data.status || (data.meeting && data.meeting.status) || 'all';
      
      // Mark tabs as needing HIGH PRIORITY refresh
      markTabAsUpdated(status, true);
      markTabAsUpdated('all', true);
      
      // Invalidate affected cache
      invalidateCache(`admin:meetings:${status}:`);
      invalidateCache('admin:meetings:all:');
    });
  }
  
  // Register handlers for meeting approvals with deduplication
  if (callbacks.onMeetingApproved) {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_APPROVED, enhancedMeetingApproved);
  } else {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_APPROVED, (data) => {
      // Deduplicate events based on meeting ID
      const meetingId = getMeetingId(data);
      
      if (meetingId) {
        const eventKey = `approved:${meetingId}`;
        const now = Date.now();
        
        // Skip if this event was already processed recently
        if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
          console.log(`Skipping duplicate meeting approved event for ${meetingId}`);
          return;
        }
        
        // Mark this event as processed
        processedEvents.set(eventKey, now);
      }
      
      // Mark affected tabs with HIGH PRIORITY
      markTabAsUpdated('pending', true);
      markTabAsUpdated('approved', true);
      markTabAsUpdated('all', true);
      
      // Invalidate affected cache
      invalidateCache('admin:meetings:pending:');
      invalidateCache('admin:meetings:approved:');
      invalidateCache('admin:meetings:all:');
    });
  }
  
  // Register handlers for meeting rejections with deduplication
  if (callbacks.onMeetingRejected) {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_REJECTED, enhancedMeetingRejected);
  } else {
    socket.on(ADMIN_SOCKET_EVENTS.MEETING_REJECTED, (data) => {
      // Deduplicate events based on meeting ID
      const meetingId = getMeetingId(data);
      
      if (meetingId) {
        const eventKey = `rejected:${meetingId}`;
        const now = Date.now();
        
        // Skip if this event was already processed recently
        if (processedEvents.has(eventKey) && now - processedEvents.get(eventKey)! < EVENT_EXPIRY) {
          console.log(`Skipping duplicate meeting rejected event for ${meetingId}`);
          return;
        }
        
        // Mark this event as processed
        processedEvents.set(eventKey, now);
      }
      
      // Mark affected tabs with HIGH PRIORITY
      markTabAsUpdated('pending', true);
      markTabAsUpdated('rejected', true);
      markTabAsUpdated('all', true);
      
      // Invalidate affected cache
      invalidateCache('admin:meetings:pending:');
      invalidateCache('admin:meetings:rejected:');
      invalidateCache('admin:meetings:all:');
    });
  }
  
  // User events - less frequent, so no deduplication needed
  if (callbacks.onUserRoleChanged) {
    socket.on(ADMIN_SOCKET_EVENTS.USER_ROLE_CHANGED, callbacks.onUserRoleChanged);
  }
  
  if (callbacks.onUserStatusChanged) {
    socket.on(ADMIN_SOCKET_EVENTS.USER_STATUS_CHANGED, callbacks.onUserStatusChanged);
  }
  
  // Throttle pending count requests
  if (callbacks.onPendingCount) {
    socket.on('admin:pending-count', callbacks.onPendingCount);
  }
  
  // Get initial pending count, but rate limit it
  safelyEmitEvent('admin:get-pending-count');
  
  // Return cleanup function
  return () => {
    if (callbacks.onMeetingCreated) {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_CREATED, enhancedMeetingCreated);
    } else {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_CREATED);
    }
    
    if (callbacks.onMeetingUpdated) {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_UPDATED, enhancedMeetingUpdated);
    } else {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_UPDATED);
    }
    
    if (callbacks.onMeetingApproved) {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_APPROVED, enhancedMeetingApproved);
    } else {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_APPROVED);
    }
    
    if (callbacks.onMeetingRejected) {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_REJECTED, enhancedMeetingRejected);
    } else {
      socket.off(ADMIN_SOCKET_EVENTS.MEETING_REJECTED);
    }
    
    if (callbacks.onUserRoleChanged) {
      socket.off(ADMIN_SOCKET_EVENTS.USER_ROLE_CHANGED, callbacks.onUserRoleChanged);
    }
    
    if (callbacks.onUserStatusChanged) {
      socket.off(ADMIN_SOCKET_EVENTS.USER_STATUS_CHANGED, callbacks.onUserStatusChanged);
    }
    
    if (callbacks.onPendingCount) {
      socket.off('admin:pending-count', callbacks.onPendingCount);
    }
  };
};

// Get the current active tab
let _currentTab: string | null = null;
export const setCurrentTab = (tab: string): void => {
  _currentTab = tab;
};
export const getCurrentTab = (): string | null => {
  return _currentTab;
};

// Cleanup admin socket connection
export const cleanupAdminSocket = (): void => {
  try {
    const socket = getSocket();
    
    if (!socket) {
      return;
    }
    
    // Leave admin room
    socket.emit('admin:leave');
    
    logger.info('Admin socket cleaned up');
  } catch (error: any) {
    logger.error(`Admin socket cleanup error: ${error.message}`);
  }
};
